<template>
  <div ref="videoContainer" class="w-full relative">
    <video
      v-if="cldnSrc"
      ref="video"
      :autoplay="autoplay"
      loop
      :muted="muted"
      playsinline
      :src="cldnSrc"
      class="max-w-none block w-full h-full object-cover"
      :controls="showControls"
      :loading="imagePriority.isHighPriority ? 'eager' : 'lazy'"
      :fetchpriority="imagePriority.isHighPriority ? 'high' : 'auto'"
      :data-poster="posterUrl"
    >
      Your browser does not support the video format.
    </video>

    <CldnImg
      v-if="!isLoaded"
      :src="src"
      :video-preview="true"
      :alt="alt"
      sizes="100vw sm:100vw md:100vw lg:100vw xl:100vw"
      class="max-w-none block w-full h-full object-cover absolute inset-0"
      :loading="imagePriority.isHighPriority ? 'eager' : 'lazy'"
    />
  </div>
</template>

<script>
import CldnImg from '@/components/CldnImg';
import { RESOLUTIONS } from '~/plugins/cloudinary.js';

export default defineNuxtComponent({
  components: { CldnImg },

  props: {
    src: {
      type: String,
      required: true,
    },
    alt: {
      type: String,
      default: '',
    },

    // Modifications
    width: {
      type: Number,
      default: 0,
    },
    height: {
      type: Number,
      default: 0,
    },
    ratio: {
      type: [Number, String],
      default: null,
    },
    quality: {
      type: [Number, String],
      default: 'auto',
    },
    crop: {
      type: String,
      default: null,
    },

    // Controls / UI
    autoplay: {
      type: Boolean,
      default: true,
    },
    muted: {
      type: Boolean,
      default: true,
    },
    controls: {
      type: Boolean,
      default: false,
    },
    isThumbnail: {
      type: Boolean,
      default: false,
    },
    poster: {
      type: [String, Boolean],
      default: true,
    },
  },

  data() {
    return {
      isLoaded: false,
      closestBreakpoint: null,
      cldnSrc: null,
      posterUrl: null,
    };
  },

  inject: {
    imagePriority: {
      from: 'imagePriority',
      default: () => ({ isHighPriority: false }),
    },
  },

  computed: {
    videoModifiers() {
      const modifiers = {
        quality: this.quality,
      };

      if (this.ratio) {
        modifiers.aspectRatio = this.ratio;
      }

      if (this.width) {
        modifiers.width = this.width;
      }

      if (this.height) {
        modifiers.height = this.height;
      }

      return modifiers;
    },

    adaptiveHeight() {
      return this.height
        ? this.height
        : this.ratio
          ? (this.closestBreakpoint * parseFloat(this.ratio)).toFixed()
          : null;
    },
    showControls() {
      return this.controls === true;
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.setupVideo();
      window.addEventListener('resize', this.setupVideo);
    });
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.setupVideo);
  },

  methods: {
    /**
     * Setup the video controls depending if the video has sound or not
     * relies heavily on cross-browser implementation, might be a subject to change
     */
    hasAudio(video) {
      return (
        video.mozHasAudio ||
        Boolean(video.webkitAudioDecodedByteCount) ||
        Boolean(video.audioTracks && video.audioTracks.length)
      );
    },

    setSoundControls() {
      const video = this.$refs.video;
      if (!video || this.controls === false) return;
      if (this.autoplay && this.muted) video.controls = false;
      else if (this.hasAudio(video) && !this.isThumbnail) video.controls = true;
    },

    setupVideo() {
      if (!this.updateCurrentBreakpoint()) return;
      this.setCldnSrc();
      this.setPosterUrl();
      this.$nextTick(() => {
        this.$refs.video?.addEventListener('loadeddata', this.showVideo);
      });
    },

    setCldnSrc() {
      const config = this.$cldn.imgUrl(this.src);
      const img = useImage();
      const imageUrl = img(config.path, this.videoModifiers, { provider: config.provider });

      this.cldnSrc = imageUrl.replace('image/upload', 'video/upload');
    },

    setPosterUrl() {
      if (this.poster === false) {
        this.posterUrl = null;
        return;
      }

      if (typeof this.poster === 'string') {
        this.posterUrl = this.poster;
        return;
      }

      const config = this.$cldn.imgUrl(this.src);
      if (!config) {
        this.posterUrl = null;
        return;
      }

      // Remove query parameters and file extension from path
      const pathWithoutQuery = config.path.split('?')[0];
      const pathWithoutExtension = pathWithoutQuery.replace(/\.[^.]+$/, '');

      // Build Cloudinary poster URL: /video/upload/so_0.0/e_preview/path.jpg
      const { cloudinaryBaseUrl } = this.$config.public;
      this.posterUrl = `${cloudinaryBaseUrl}/video/upload/so_0.0/e_preview/${pathWithoutExtension}.jpg`;
    },

    updateCurrentBreakpoint() {
      const containerWidth = Math.round(Number(this.$refs.videoContainer?.getBoundingClientRect().width));
      const lastBreakpointValue = this.closestBreakpoint;
      this.closestBreakpoint = this.getRelatedBreakpoint(containerWidth);
      return !(!containerWidth || lastBreakpointValue === this.closestBreakpoint);
    },

    showVideo() {
      this.isLoaded = true;
      this.setSoundControls();
      this.$refs.video?.removeEventListener('loadeddata', this.showVideo);
    },

    getRelatedBreakpoint(width) {
      return RESOLUTIONS.find((res) => width <= res) || RESOLUTIONS.slice(-1)[0];
    },
  },
});
</script>
